import { DialogPanel, DialogTitle } from '@headlessui/react';
import { ReactElement, useEffect } from 'react';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import { MemoForm } from 'elements/MemoForm';
import useMediaQuery from 'hooks/useMediaQuery';
import { useModal } from 'xstate/hooks/useModal';
import { closeModal } from 'xstate/actors/ModalActor';
import { MemoFormModalPayload } from 'xstate/machines/ModalMachine';
import { useTranslations } from 'next-intl';

// The mobile memo composer. Opened as a create sheet from the dashboard FAB, or
// as an edit sheet when editing a memo on mobile. A memo in the payload means
// edit mode. Closes itself once the memo is saved or the sheet is dismissed.
const MemoFormModal = (): ReactElement => {
  const t = useTranslations();
  const { payload } = useModal() as { payload: MemoFormModalPayload };
  const isEditing = !!payload?.memo;

  // This composer is mobile only; the desktop layout uses the inline form. If
  // the viewport grows to desktop while it's open, close it so it never lingers
  // on top of the desktop UI.
  const isDesktop = useMediaQuery('sm');
  useEffect(() => {
    if (isDesktop) closeModal();
  }, [isDesktop]);

  return (
    <div className={styles.outer}>
      <div className={styles.inner}>
        <DialogPanel transition className={styles.panel}>
          <div className={styles.header.container}>
            <DialogTitle as="h2" className={styles.header.title}>
              {isEditing
                ? t('memoComposer.titleEdit')
                : t('memoComposer.titleCreate')}
            </DialogTitle>
            <button
              type="button"
              aria-label={t('common.close')}
              onClick={closeModal}
              className={styles.header.close}
            >
              <FontAwesomeIcon icon={faXmark} />
            </button>
          </div>

          <div className={styles.body}>
            <MemoForm
              memo={payload?.memo}
              defaultCollection={payload?.defaultCollection}
              defaultDatedAt={payload?.defaultDatedAt}
              onSuccess={closeModal}
            />
          </div>
        </DialogPanel>
      </div>
    </div>
  );
};

const styles = {
  outer: 'fixed inset-0 z-10 w-screen overflow-y-auto',
  // Anchor the sheet near the top so the collection dropdown (which opens
  // downward) always has room below it, even on short screens.
  inner: 'flex min-h-full items-start justify-center p-3 sm:p-4',
  panel: classNames(
    'flex max-h-[calc(100vh-1.5rem)] w-full max-w-[580px] flex-col overflow-hidden bg-surface-card text-ink-primary shadow-xl',
    'rounded-2xl',
    'transform transition-all data-closed:-translate-y-4 data-closed:opacity-0',
    'data-enter:duration-300 data-leave:duration-200 data-enter:ease-out data-leave:ease-in',
  ),
  header: {
    container:
      'flex items-center justify-between border-b border-hairline px-4 py-3',
    title: 'text-base font-bold text-mf-off-black-950',
    close:
      'flex h-8 w-8 items-center justify-center rounded-full text-ink-secondary hover:bg-surface-field',
  },
  body: 'flex-1 overflow-y-auto px-3 py-4',
};

export default MemoFormModal;
